/**
 * Whether a keyboard is covering the chat, and how tall the chat may therefore be.
 *
 * **Its own module, with no DOM in it, for `scroll.ts`'s reason**: this is the decision four
 * shipped fixes got wrong, and a decision that cannot be tested is one that gets argued about
 * instead. Everything here takes plain numbers, so `packages/web/test/chat-viewport.test.ts`
 * can assert it against readings captured from the handset that had the bug. `viewport.ts`
 * keeps the parts that genuinely need a browser — reading `visualViewport`, writing the custom
 * property, listening for the events — and holds no policy of its own.
 */

/**
 * Below this, a reported inset is not treated as a keyboard.
 *
 * `visualViewport` and `window.innerHeight` disagree by a pixel or two at rest on several
 * browsers — rounding, a scrollbar, a rubber-band still settling — and clamping on that would
 * take a stray pixel off the chat and flicker as the page moves. Well below any real keyboard,
 * which is a third of the screen, and deliberately generous: the costly direction is reporting
 * no keyboard when there is one, because that is the composer disappearing again.
 */
export const KEYBOARD_FLOOR_PX = 60;

/** The visible window, in the terms the two APIs report it. */
export interface ViewportReading {
  /** `window.innerHeight` — the *layout* viewport, which iOS does not shrink for a keyboard. */
  innerHeight: number;
  /** `visualViewport.height` — the only reading that moves when the keyboard opens. */
  height: number;
  /** `visualViewport.offsetTop` — iOS sliding the visible window to reveal a focused field. */
  offsetTop: number;
  /**
   * Whether something editable currently has focus.
   *
   * **A keyboard cannot exist without one, and this is the guard the first version lacked.**
   * The arithmetic below decides *how much* is covered; it was also being asked to decide
   * *whether* anything is, and it is not reliable at that on its own — `visualViewport.height`
   * is briefly short during a reload, an orientation change, and while iOS animates its own
   * chrome. One such reading with nothing to revise it leaves a clamp measured from a moment
   * that never really existed, and the chat sits short with dead space beneath it for the rest
   * of the session.
   *
   * That reached both learners. The second time was minutes after a reload, which is exactly
   * when a transient measurement is most likely and least likely to be followed by another
   * event.
   *
   * Focus is the honest precondition: iOS raises the keyboard for a focused editable element
   * and for nothing else, so no focus means no keyboard, whatever the viewport momentarily
   * says. It also fails in the safe direction — a keyboard genuinely up while focus has moved
   * on is a chat that is briefly too tall, which scrolls, where the opposite is a composer
   * nobody can see.
   */
  focused: boolean;
}

/**
 * The height the chat may not exceed, or `undefined` when nothing is covering the viewport.
 *
 * Pure, and separated from the hook deliberately: this is the third learner-visible bug to
 * come out of this decision, and each previous one was a belief about iOS that could have been
 * settled by a test against readings from a handset. Those readings are now in
 * `packages/web/test/chat-viewport.test.ts`, taken from the installed app with the keyboard
 * both open and closed.
 *
 * `undefined` rather than a full height is the load-bearing half of the return type. It maps
 * to removing the custom property, so `max-height: var(--chat-view-h, none)` resolves to
 * `none` and the shell's flex arithmetic — the layout every other route uses — decides the
 * size. Returning a measured full height instead would make this file responsible for a number
 * CSS already knows, and wrong the moment a header wraps to two lines.
 */
export function chatCeilingFor(view: ViewportReading, rectTop: number): number | undefined {
  // No focus, no keyboard. See `focused`: without this a transient viewport reading becomes a
  // permanent clamp, which is how half a screen of dead space reached two learners.
  if (!view.focused) return undefined;

  const visibleBottom = view.offsetTop + view.height;
  /*
   * Is anything covering the viewport? This is the same subtraction two earlier attempts got
   * wrong, and it is safe here only because of how weakly it is used: they computed the layout
   * *from* it, and this only decides whether to clamp at all.
   */
  const covered = Math.round(view.innerHeight - visibleBottom);
  if (covered < KEYBOARD_FLOOR_PX) return undefined;
  /*
   * Measured against this element's own top edge rather than derived from `innerHeight`.
   * `getBoundingClientRect()` and `visualViewport` share a coordinate frame, so the visible
   * window is `[offsetTop, offsetTop + height]` and the answer is the distance from the top of
   * the element to the bottom of that window — no assumption about the layout viewport, which
   * is the only assumption that has been wrong here.
   */
  return Math.max(0, Math.round(visibleBottom - rectTop));
}
